import { useEffect, useRef } from "react";

export default function ImageViewer({ items = [], activeIndex, onClose, onChange, title = "Image viewer" }) {
  const dialog = useRef(null);
  const previouslyFocused = useRef(null);
  const active = activeIndex == null ? null : items[activeIndex];
  const hasMany = items.length > 1;
  const isOpen = activeIndex != null;

  useEffect(() => {
    if (!isOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    previouslyFocused.current = document.activeElement;
    document.body.style.overflow = "hidden";
    dialog.current?.showModal();
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  const close = () => {
    dialog.current?.close();
    onClose();
    previouslyFocused.current?.focus?.();
  };

  const move = (step) => {
    if (!hasMany) return;
    const next = (activeIndex + step + items.length) % items.length;
    onChange(next);
  };

  if (!active) return null;

  return (
    <dialog
      ref={dialog}
      aria-labelledby="sn-image-viewer-title"
      onCancel={(event) => {
        event.preventDefault();
        close();
      }}
      onKeyDown={(event) => {
        if (event.key === "ArrowRight") move(1);
        if (event.key === "ArrowLeft") move(-1);
      }}
      onClick={(event) => {
        if (event.target !== dialog.current) return;
        const bounds = dialog.current.getBoundingClientRect();
        if (
          event.clientX < bounds.left ||
          event.clientX > bounds.right ||
          event.clientY < bounds.top ||
          event.clientY > bounds.bottom
        ) {
          close();
        }
      }}
      className="sn-video-dialog sn-m-auto sn-w-[calc(100%-2rem)] sn-max-w-5xl sn-max-h-[calc(100dvh-2rem)] sn-overflow-y-auto sn-rounded-card sn-bg-cream sn-p-4 sn-text-ink md:sn-p-6"
    >
      <div className="sn-mb-4 sn-flex sn-items-start sn-justify-between sn-gap-4">
        <div>
          <p className="sn-text-[11px] sn-font-bold sn-uppercase sn-tracking-[0.18em] sn-text-leaf">{title}</p>
          <h2 id="sn-image-viewer-title" className="sn-mt-2 sn-font-display sn-text-xl sn-leading-tight sn-text-forest">{active.title}</h2>
        </div>
        <button type="button" autoFocus onClick={close} className="sn-min-h-[44px] sn-min-w-[64px] sn-font-semibold sn-text-forest">Close</button>
      </div>
      <div className="sn-relative">
        <img src={active.src} alt={active.alt} className="sn-mx-auto sn-max-h-[70dvh] sn-w-auto sn-max-w-full sn-rounded sn-object-contain" />
        {hasMany && (
          <div className="sn-mt-4 sn-flex sn-items-center sn-justify-between sn-gap-4">
            <button type="button" onClick={() => move(-1)} className="sn-min-h-[44px] sn-rounded sn-border sn-border-line sn-px-4 sn-text-sm sn-font-semibold sn-text-forest">Previous</button>
            <p className="sn-text-sm sn-text-muted">{activeIndex + 1} of {items.length}</p>
            <button type="button" onClick={() => move(1)} className="sn-min-h-[44px] sn-rounded sn-border sn-border-line sn-px-4 sn-text-sm sn-font-semibold sn-text-forest">Next</button>
          </div>
        )}
      </div>
    </dialog>
  );
}
